'use client';

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { useTranslation } from "../../hooks/useTranslation";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { Spinner } from "../../components/loading-spinner";
import { getWeekFixtureLabel } from "../../lib/weekFixtureLabel";

interface RankingEntry {
  userId: string;
  correctPredictions: number;
  totalPredictions: number;
  accuracy: number;
  userName?: string;
  teamName?: string;
  rank: number;
  score: number;
}

interface RankingResponse {
  data: RankingEntry[];
  totalRecords: number;
  currentPage: number;
  totalPages: number;
  userRank?: RankingEntry;
}

interface UserRanksResponse {
  generalRank: RankingEntry | null;
  weekRank: RankingEntry | null;
}

const RECORDS_PER_PAGE = 10;

export default function Ranking({ teamId, userId, isAdmin }: { teamId?: number; userId?: string; isAdmin?: boolean }) {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const { competitionData, competitionConfig } = useCompetitionContext();
  const { t } = useTranslation();

  // Don't render anything if competitionConfig is not ready
  if (!competitionConfig?.competitionFeed || !competitionConfig?.seasonId) {
    return (
      <div className="flex min-h-[200px] items-center justify-center px-3 pb-8 pt-6 sm:px-4">
        <Spinner size={24} className="text-white" />
      </div>
    );
  }

  // State
  const [activeTab, setActiveTab] = useState<'general' | 'week' | 'leagues' | 'leaguesWeek'>('general');
  const [rankings, setRankings] = useState<RankingEntry[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchName, setSearchName] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [userRanks, setUserRanks] = useState<UserRanksResponse | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  const tabs = [
    { name: t('ranking.tabs.general'), key: 'general', current: activeTab === 'general' },
    { name: t('ranking.tabs.week'), key: 'week', current: activeTab === 'week' },
    // { name: t('ranking.tabs.leagues'), key: 'leagues', current: activeTab === 'leagues' },
    // { name: t('ranking.tabs.leaguesWeek'), key: 'leaguesWeek', current: activeTab === 'leaguesWeek' },
  ];

  // Sync active tab with route param so /fantasy/rankings/general and /fantasy/rankings/week can deep-link.
  useEffect(() => {
    if (!tab) return;
    if (tab === 'general' || tab === 'week') {
      setActiveTab(tab);
      setPageNumber(1);
      setSearchName('');
      setSearchInput('');
      return;
    }
    // Unknown tab -> fallback to general
    setActiveTab('general');
    setPageNumber(1);
    setSearchName('');
    setSearchInput('');
  }, [tab]);

  // Function to fetch user ranks (both general and weekly)
  const fetchUserRanks = useCallback(async () => {
    if (!userId || !competitionConfig?.competitionFeed || !competitionConfig?.seasonId) return;

    try {
      const params = new URLSearchParams({
        competitionFeed: competitionConfig.competitionFeed,
        seasonId: String(competitionConfig.seasonId),
        userId: userId,
      });

      if (selectedWeekId) {
        params.append('weekId', String(selectedWeekId));
      }

      const response = await fetch(`/api/rankings/user?${params}`);
      if (!response.ok) throw new Error(`Failed to fetch user ranks: ${response.statusText}`);

      const data: UserRanksResponse = await response.json();
      setUserRanks(data);

      // Auto-navigate to user's page on initial load only (not on every fetch)
      if (activeTab === 'general' && data.generalRank && pageNumber === 1 && !hasInitialized) {
        const targetPage = Math.ceil(data.generalRank.rank / RECORDS_PER_PAGE);
        setPageNumber(targetPage);
      } else if (activeTab === 'week' && data.weekRank && pageNumber === 1 && !hasInitialized) {
        const targetPage = Math.ceil(data.weekRank.rank / RECORDS_PER_PAGE);
        setPageNumber(targetPage);
      }
    } catch (error) {
      console.error('Error fetching user ranks:', error);
    }
  }, [userId, competitionConfig, selectedWeekId, activeTab, hasInitialized, pageNumber]);

  // Function to fetch rankings data
  const fetchRankings = useCallback(async () => {
    if (!competitionConfig?.competitionFeed || !competitionConfig?.seasonId) return;
    
    // For leagues tabs, skip if not implemented yet
    if (activeTab === 'leagues' || activeTab === 'leaguesWeek') {
      setIsLoading(false);
      return;
    }

    if (activeTab === 'week' && selectedWeekId === null) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        competitionFeed: competitionConfig.competitionFeed,
        seasonId: String(competitionConfig.seasonId),
        pageNumber: String(pageNumber),
        pageRecords: String(RECORDS_PER_PAGE),
      });

      if (searchName) {
        params.append('name', searchName);
      }

      if (userId) {
        params.append('userId', userId);
      }

      if (activeTab === 'week' && selectedWeekId) {
        params.append('weekId', String(selectedWeekId));
      }

      // Title filter (for future implementation if needed)
      if (selectedTitle && selectedTitle !== 'all') {
        params.append('title', selectedTitle);
      }

      const response = await fetch(`/api/rankings?${params}`);
      if (!response.ok) throw new Error(`Failed to fetch rankings: ${response.statusText}`);

      const data: RankingResponse = await response.json();
      setRankings(data.data);
      setTotalRecords(data.totalRecords);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching rankings:', error);
      setRankings([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [competitionConfig, activeTab, pageNumber, searchName, selectedWeekId, selectedTitle, userId]);

  // Initialize selectedWeekId once when competitionData is available
  useEffect(() => {
    if (!hasInitialized && competitionData?.deadlineInfo) {
      const { deadlineWeek, displayWeek, rosterEndWeek } = competitionData.deadlineInfo;
      // Default weekId should be the deadlineWeek (no -1 offset)
      const defaultWeekId =
        (typeof deadlineWeek === 'number' ? deadlineWeek : null) ??
        (typeof displayWeek === 'number' ? displayWeek : null) ??
        (typeof rosterEndWeek === 'number' ? rosterEndWeek : null) ??
        1;
      setSelectedWeekId(defaultWeekId);
      setHasInitialized(true);
    }
  }, [competitionData, hasInitialized]);

  // OPTIMIZED: Fetch both user ranks and rankings in parallel
  useEffect(() => {
    if (!hasInitialized || selectedWeekId === null) return;
    
    // Skip if week tab but no week selected
    if (activeTab === 'week' && selectedWeekId === null) return;
    
    // Parallelize API calls for better performance
    const promises: Promise<void>[] = [];
    
    // Fetch user ranks if userId provided
    if (userId && (activeTab === 'general' || activeTab === 'week')) {
      promises.push(fetchUserRanks());
    }
    
    // Fetch rankings
    promises.push(fetchRankings());
    
    // Execute in parallel
    Promise.all(promises).catch((error) => {
      console.error('Error fetching ranking data:', error);
    });
  }, [hasInitialized, selectedWeekId, userId, activeTab, fetchUserRanks, fetchRankings]);

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchName(searchInput.trim());
    setPageNumber(1);
  };

  // Handle tab change - reset to first page
  const handleTabChange = (newTab: 'general' | 'week' | 'leagues' | 'leaguesWeek') => {
    setActiveTab(newTab);
    setPageNumber(1);
    setSearchName('');
    setSearchInput('');
    // Keep URL in sync for deep links (only for supported public tabs)
    if (newTab === 'general' || newTab === 'week') {
      navigate(`/fantasy/rankings/${newTab}`);
    }
  };

  function classNames(...classes: any) {
    return classes.filter(Boolean).join(' ');
  }

  const formatDisplayedScore = (score: number) => Math.floor(score);

  const paginationBtnClass =
    "bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200";

  return (
    <div className="min-h-[calc(100vh-100px)] px-3 pb-20 pt-6 sm:px-4">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div>
          <h1 className="mb-2 text-xl font-bold text-white">{t("ranking.title")}</h1>
          <p className="text-md text-white/90">{t("ranking.intro")}</p>
        </div>

      {/* Mobile Tab Selector */}
      <div className="relative grid grid-cols-1 sm:hidden">
        <select
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value as any)}
          aria-label="Select a tab"
          className="w-full appearance-none rounded-full border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] py-2.5 pl-4 pr-10 text-base font-semibold text-white outline-none focus:ring-2 focus:ring-[#FDC300]/50"
        >
          {tabs.map((tab) => (
            <option key={tab.key} value={tab.key} className="bg-[var(--rtbf-blue-dark,#003E63)] text-white">
              {tab.name}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-white/80"
        />
      </div>

      {/* Desktop Tabs */}
      <div className="hidden sm:block">
        <nav aria-label="Tabs" className="-mb-px flex flex-wrap gap-6 border-b border-white/20">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key as any)}
              aria-current={tab.current ? "page" : undefined}
              className={classNames(
                tab.current
                  ? "border-[#FDC300] text-white"
                  : "border-transparent text-white/70 hover:border-white/40 hover:text-white",
                "whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-semibold transition-colors",
              )}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* User Rank Display */}
      {userRanks && userId && (userRanks.generalRank || userRanks.weekRank) ? (
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-white">{t("ranking.yourPosition")}</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
            {userRanks.generalRank && (
              <div
                className="cursor-pointer rounded-lg border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] p-4 text-white transition-colors hover:bg-[var(--rtbf-blue,#0076A9)]/40"
                onClick={() => {
                  const targetPage = Math.ceil(userRanks.generalRank!.rank / RECORDS_PER_PAGE);
                  if (activeTab !== "general") {
                    handleTabChange("general");
                  }
                  setPageNumber(targetPage);
                }}
              >
                <div className="text-sm text-white/80">{t("ranking.generalRanking")}</div>
                <div className="text-lg font-semibold text-[#FDC300]">#{userRanks.generalRank.rank}</div>
                <div className="text-sm text-white/90">
                  {formatDisplayedScore(userRanks.generalRank.score)} {t("ranking.points")}
                </div>
                <div className="mt-2 text-xs text-[var(--rtbf-cyan,#1DD5F0)]">{t("ranking.clickToGoToPosition")}</div>
              </div>
            )}
            {userRanks.weekRank && (
              <div
                className="cursor-pointer rounded-lg border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] p-4 text-white transition-colors hover:bg-[var(--rtbf-blue,#0076A9)]/40"
                onClick={() => {
                  const targetPage = Math.ceil(userRanks.weekRank!.rank / RECORDS_PER_PAGE);
                  if (activeTab !== "week") {
                    handleTabChange("week");
                  }
                  setPageNumber(targetPage);
                }}
              >
                <div className="text-sm text-white/80">{t("ranking.weekRanking").replace("{week}", String(selectedWeekId))}</div>
                <div className="text-lg font-semibold text-[#FDC300]">#{userRanks.weekRank.rank}</div>
                <div className="text-sm text-white/90">
                  {formatDisplayedScore(userRanks.weekRank.score)} {t("ranking.points")}
                </div>
                <div className="mt-2 text-xs text-[var(--rtbf-cyan,#1DD5F0)]">{t("ranking.clickToGoToPosition")}</div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Name search above week filter (general + week) */}
      <div className="flex w-full flex-col gap-4">
        {/* {(activeTab === 'general' || activeTab === 'week') && (
          <form className="w-full" onSubmit={handleSearch}>
            <Input
              type="search"
              enterKeyHint="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('ranking.searchPlaceholder')}
              aria-label={t('ranking.searchPlaceholder')}
              className="h-10 w-full rounded-md border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white placeholder:text-white/50 focus-visible:ring-[#FDC300]/50"
            />
          </form>
        )} */}
        {(activeTab === 'week' || activeTab === 'leaguesWeek') && (
          <div className="w-full">
            <Select
              value={selectedWeekId != null ? String(selectedWeekId) : 'unset'}
              onValueChange={(value: string) => {
                setPageNumber(1);
                setSelectedWeekId(value === 'unset' ? null : Number(value));
              }}
            >
              <SelectTrigger
                aria-label={t("ranking.filters.matchday")}
                className="w-full border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white"
              >
                <SelectValue placeholder={t('ranking.filters.selectMatchday')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">{t('ranking.filters.noMatchdaySelected')}</SelectItem>
                {competitionData.weeks.map((week: any) => (
                  <SelectItem key={week.weekId} value={String(week.weekId)}>
                    {getWeekFixtureLabel(week.weekId, competitionData)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Rankings Table */}
      <div className="overflow-hidden rounded-md text-white">
        <Table className="min-w-full text-sm">
          <TableHeader className="bg-[var(--rtbf-blue,#0076A9)] [&_tr]:border-[var(--rtbf-blue,#0076A9)]">
            <TableRow className="border-b-0 hover:bg-transparent">
              <TableHead className="w-16 py-3 font-medium text-white"></TableHead>
              <TableHead className="py-3 font-medium text-white">
                {["leagues", "leaguesWeek"].includes(activeTab) ? t("ranking.table.friendLeague") : t("ranking.table.user")}
              </TableHead>
              <TableHead className="py-3 text-right font-medium text-white">{t("ranking.table.points")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                <TableCell colSpan={3} className="py-8 text-center text-white">
                  <div className="flex items-center justify-center gap-2">
                    <Spinner size={20} className="text-white/80" />
                    <span className="text-white/80">{t("ranking.loading")}</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : rankings.length === 0 ? (
              <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                <TableCell colSpan={3} className="py-6 text-center text-white/90">
                  {["leagues", "leaguesWeek"].includes(activeTab) ? t("ranking.leaguesNotAvailable") : t("ranking.noResults")}
                </TableCell>
              </TableRow>
            ) : (
              rankings.map((entry: RankingEntry, idx: number) => {
                const isCurrent = userId && entry.userId === userId;
                const rowBg = idx % 2 === 0 ? "bg-[var(--rtbf-blue-dark,#003E63)]" : "bg-[var(--rtbf-blue,#0076A9)]";

                if (["leagues", "leaguesWeek"].includes(activeTab)) {
                  return (
                    <TableRow
                      key={idx}
                      className={classNames(rowBg, "border-b border-[var(--rtbf-blue,#0076A9)] text-white hover:bg-white/10")}
                    >
                      <TableCell className="font-medium text-white">{entry.rank}</TableCell>
                      <TableCell className="text-white">{t("ranking.leagueDataComingSoon")}</TableCell>
                      <TableCell className="text-right text-white">-</TableCell>
                    </TableRow>
                  );
                }

                const handleRowClick = () => {
                  if (isAdmin && entry.userId) {
                    navigate(`/fantasy/user-points/${entry.userId}`, { state: { from: "rankings" } });
                  }
                };

                return (
                  <TableRow
                    key={entry.userId}
                    className={classNames(
                      rowBg,
                      "border-b border-[var(--rtbf-blue,#0076A9)] text-white hover:bg-white/10",
                      isCurrent && "ring-2 ring-inset ring-[#FDC300]",
                      isAdmin && "cursor-pointer",
                    )}
                    onClick={handleRowClick}
                  >
                    <TableCell className="font-medium text-white">{entry.rank}</TableCell>
                    <TableCell className="text-white">
                      <div className={isCurrent ? "font-semibold" : ""}>
                        {entry.userName || t("ranking.anonymous")}
                        {isCurrent && (
                          <span className="ml-2 rounded bg-[#FDC300]/25 px-2 py-0.5 text-xs text-[#FDC300]">{t("ranking.you")}</span>
                        )}
                        {isAdmin && (
                          <span className="ml-2 rounded bg-white/15 px-2 py-0.5 text-xs text-white/90">{t("ranking.viewAdmin")}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium text-white">{formatDisplayedScore(entry.score)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-end gap-4 p-2 sm:flex-row">
        <p className="text-sm text-white/90">
          {t("ranking.pagination.showing")}{" "}
          <span className="font-medium text-white">{(pageNumber - 1) * RECORDS_PER_PAGE + 1}</span>{" "}
          {t("ranking.pagination.to")}{" "}
          <span className="font-medium text-white">{Math.min(pageNumber * RECORDS_PER_PAGE, totalRecords)}</span>{" "}
          {t("ranking.pagination.of")} <span className="font-medium text-white">{totalRecords}</span>{" "}
          {t("ranking.pagination.results")}
        </p>
        <nav className="flex items-center gap-2 text-sm">
          <Button variant="outline" size="sm" className={paginationBtnClass} onClick={() => setPageNumber(1)} disabled={pageNumber === 1}>
            1
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={paginationBtnClass}
            onClick={() => setPageNumber((p: number) => Math.max(1, p - 1))}
            disabled={pageNumber === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[2.5rem] rounded-md bg-[var(--rtbf-blue,#0076A9)] px-3 py-1.5 text-center text-sm font-bold text-white">
            {pageNumber}
          </span>
          <Button
            variant="outline"
            size="sm"
            className={paginationBtnClass}
            onClick={() => setPageNumber((p: number) => Math.min(totalPages, p + 1))}
            disabled={pageNumber === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className={paginationBtnClass} onClick={() => setPageNumber(totalPages)} disabled={pageNumber === totalPages}>
            {totalPages}
          </Button>
        </nav>
      </div>
      </div>
    </div>
  );
}
